import { subDays } from "date-fns";
import moment from "moment";

import { Dispatch, GetState } from "common/types/redux";

import { getClient } from "services";
import errorHandler from "services/assignmentsService/errorHandler";

import { isSignedIn } from "seneca-common/features/user/state";
import { formatDateWithSlash } from "seneca-common/utils/functions/format";
import { awaitCondition } from "seneca-common/utils/state/await-condition";

import { Contents, getAllContents } from "../../../../course/state";
import {
  allStudentAssignmentsFetchFinished,
  allStudentAssignmentsNeedFetching,
  childAssignmentNeedsFetching,
  childAssignmentsFetchFinished,
  childAssignmentsNeedFetching,
  currentStudentAssignmentsFetchFinished,
  currentStudentAssignmentsNeedFetching,
  fetchAllStudentAssignmentsActionFactory,
  fetchChildAssignmentActionFactory,
  fetchChildAssignmentsActionFactory,
  fetchCurrentStudentAssignmentsActionFactory,
  fetchStudentAssignmentActionFactory,
  studentAssignmentFetchFinished,
  studentAssignmentNeedsFetching
} from "../state";
import {
  receiveAssignmentsWithContents,
  receiveAssignmentWithContents
} from "../state/actions";

// == fetching student assignments with dueDate between [-3days, future) ==

export const fetchCurrentStudentAssignments = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    return dispatch(fetchCurrentStudentAssignmentsAction(contents)());
  };
};

const fetchCurrentStudentAssignmentsAction = (contents: Contents) =>
  fetchCurrentStudentAssignmentsActionFactory({
    requestFunction: (
      requestParams: {
        date?: string;
        archived?: boolean;
      } = {}
    ) =>
      getClient("assignmentsService").fetchStudentAssignments(
        undefined,
        requestParams.date,
        undefined,
        requestParams.archived
      ),
    storeAction: receiveAssignmentsWithContents(contents),
    errorHandler
  });

// == fetching all student assignments ==
const fetchAllStudentAssignments = (contents: Contents) =>
  fetchAllStudentAssignmentsActionFactory({
    requestFunction: () =>
      getClient("assignmentsService").fetchStudentAssignments(),
    storeAction: receiveAssignmentsWithContents(contents),
    errorHandler
  });

// == fetching single student assignment
const fetchStudentAssignmentAction = (contents: Contents) =>
  fetchStudentAssignmentActionFactory({
    requestFunction: async (assignmentId: string) =>
      getClient("assignmentsService").fetchStudentAssignments(
        undefined,
        undefined,
        undefined,
        undefined,
        assignmentId
      ),
    storeAction: receiveAssignmentsWithContents(contents),
    errorHandler
  });

function fetchStudentAssignment(assignmentId: string, contents: Contents) {
  return fetchStudentAssignmentAction(contents)([assignmentId], assignmentId);
}

export function fetchTodoStudentAssignmentsIfNotFetched() {
  const dateTwoWeeksAgo = formatDateWithSlash(subDays(new Date(), 14));
  return fetchCurrentStudentAssignmentsIfNotFetched({
    date: dateTwoWeeksAgo,
    archived: false
  });
}

export const fetchCurrentStudentAssignmentsIfNotFetched =
  ({
    date = moment().format(),
    archived
  }: {
    date: string | null;
    archived?: boolean;
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    if (isSignedIn(state)) {
      if (currentStudentAssignmentsNeedFetching(state)) {
        return dispatch(
          fetchCurrentStudentAssignmentsAction(contents)({
            date,
            archived
          })
        );
      } else {
        return dispatch(
          awaitCondition((state: any) =>
            currentStudentAssignmentsFetchFinished(state)
          )
        );
      }
    }
  };

export const fetchAllStudentAssignmentsIfNotFetched =
  () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    if (allStudentAssignmentsNeedFetching(state)) {
      return dispatch(fetchAllStudentAssignments(contents)());
    } else {
      return dispatch(
        awaitCondition((state: any) =>
          allStudentAssignmentsFetchFinished(state)
        )
      );
    }
  };

export const fetchStudentAssignmentIfNotFetched =
  (assignmentId: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    if (studentAssignmentNeedsFetching(state, assignmentId)) {
      return dispatch(fetchStudentAssignment(assignmentId, contents));
    } else {
      return dispatch(
        awaitCondition((state: any) =>
          studentAssignmentFetchFinished(state, assignmentId)
        )
      );
    }
  };

// == fetching student/child assignments

export const fetchChildAssignmentsAction = (contents: Contents) =>
  fetchChildAssignmentsActionFactory({
    requestFunction: (userId: string) =>
      getClient("assignmentsService").fetchChildAssignments(
        userId,
        undefined,
        100
      ),
    storeAction: receiveAssignmentsWithContents(contents),
    errorHandler
  });

function fetchChildAssignments(userId: string, contents: Contents) {
  return fetchChildAssignmentsAction(contents)([userId], userId);
}

export const fetchChildAssignmentsByUserIdIfNotFetched =
  ({ userId }: { userId: string }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    if (isSignedIn(state)) {
      if (childAssignmentsNeedFetching(state, userId)) {
        return dispatch(fetchChildAssignments(userId, contents));
      } else {
        return dispatch(
          awaitCondition((state: any) =>
            childAssignmentsFetchFinished(state, userId)
          )
        );
      }
    }
  };

// == fetching single student/child assignment

export const fetchChildAssignmentAction = (contents: Contents) =>
  fetchChildAssignmentActionFactory({
    requestFunction: (userId: string, assignmentId: string) =>
      getClient("assignmentsService").fetchChildAssignment(
        userId,
        assignmentId
      ),
    storeAction: receiveAssignmentWithContents(contents),
    errorHandler
  });

export function fetchChildAssignment(userId: string, assignmentId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);

    return dispatch(
      fetchChildAssignmentAction(contents)([userId, assignmentId], assignmentId)
    );
  };
}

export const fetchChildAssignmentIfNotFetched =
  ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (isSignedIn(state)) {
      if (childAssignmentNeedsFetching(state, userId, assignmentId)) {
        return dispatch(fetchChildAssignment(userId, assignmentId));
      } else {
        return dispatch(
          awaitCondition((state: any) =>
            childAssignmentNeedsFetching(state, userId, assignmentId)
          )
        );
      }
    }
  };
